import * as React from 'react';
import { UserContext } from '@app/Context/UserContext';
import ProducerInterface from '@app/Interface/ProducerInterface';
import UserAuthentication from '@app/Security/UserAuthentication';
import UserModel from '@app/Model/UserModel';
import TemplateDefinitions from '@app/utils/TemplateDefinitions';
import { ActionGroup, Alert, Button, Checkbox, Form, FormAlert, FormGroup, Modal, ModalVariant, PageSection, TextInput, Title } from '@patternfly/react-core';
import UserRoleTypesInterface from '@app/Interface/UserRoleTypesInterface';
import MyFpuListFunctions from '@app/Page/MyFpuListPage/MyFpuListFunctions';
import UserRoleDescriptions from '@app/Components/User/UserRoleDescriptions';
import { ToastNotificationType } from '@app/Interface/ToastNotificationInterface';
import { CreateToastNotification, ToastNotificationContext } from '@app/Context/ToastNotificationContext';
import ToastNotifications from '@app/Components/ToastNotification/ToastNotifications';
import FieldsInterface from '@app/Interface/FieldsInterface';
import { ExclamationCircleIcon } from '@patternfly/react-icons';
import TypeAssertions from '@app/utils/TypeAssertions';
import UserFpuAccessHelper from '@app/Service/UserFpuAccessHelper';
import FpuPermissionsComponentTypeInterface from '@app/Interface/FpuPermissionsComponentViewInterface';
import FpuPermissionsComponentTypeModel from '@app/Model/FpuPermissionsComponentViewModel';
import { PermissionDenied } from '@app/PermissionDenied/PermissionDenied';

interface FpuAddUserPermissionsProps {
    producerId: number;
    producer: ProducerInterface;
    roleTypes: UserRoleTypesInterface[];
    changeView: (component: FpuPermissionsComponentTypeInterface, fetchUsers?: boolean) => void;
}

const FpuAddUserPermissions: React.FC<FpuAddUserPermissionsProps> = ({ producerId, producer, roleTypes, changeView }) => {

    // NOTE: ToastNotificationContext provider was added to `src\app\index.tsx`
    const { toastDispatch } = React.useContext(ToastNotificationContext);

    const defaultFormValidated = "default";

    // for values: see https://www.patternfly.org/v4/components/form#formgroup
    const [requiredValidated, setRequiredValidated] = React.useState(defaultFormValidated);

    const initialFieldValues: FieldsInterface = MyFpuListFunctions.getAddUserields();

    const [formFields, setFormFields] = React.useState(initialFieldValues);

    const [userRoleTypes, setUserRoleTypes] = React.useState([...roleTypes]);

    const { userState } = React.useContext(UserContext);

    // NOTE: if user permission was changed, they would need to log out/log in to see changes.
    const canViewPage = UserAuthentication.canUserViewDashboardItem(userState.user.viewPermissions, userState.user.currentFpuId, TemplateDefinitions.TEMPLATE_VIEW_FPU_LIST_PAGE);

    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const [isFormProcessing, setIsFormProcessing] = React.useState(false);

    const component = new FpuPermissionsComponentTypeModel();

    const handleModalToggle = () => {
        setIsModalOpen(!isModalOpen);
    }

    const handleCheckBoxToggle = (checked: boolean, event: React.FormEvent<HTMLInputElement>) => {

        const roleId = event.currentTarget.value ?? 0;

        const updatedRoleTypes = [...userRoleTypes];

        const roleTypeToUpdate = updatedRoleTypes.find(roleType => roleType.id === Number(roleId));

        if (roleTypeToUpdate) {

            roleTypeToUpdate.checked = checked;

            setUserRoleTypes(updatedRoleTypes);

        }

    }

    const handleChange = (newValue: string, e: React.FormEvent<HTMLInputElement>) => {

        const fieldId = e.currentTarget.id;

        const updatedFields = { ...formFields };

        updatedFields[fieldId].value = newValue;

        setFormFields(updatedFields);

    }

    const handleSubmit = async (e: React.SyntheticEvent) => {

        e.preventDefault();

        const controller = new AbortController();

        setIsFormProcessing(true);

        const validform = formValidation();

        if (validform) {

            let notificationType: ToastNotificationType = ToastNotificationType.Default;

            let toastTitle = "";

            let toastDescription = "";

            const email = typeof formFields["emailAddress"].value === "string" ? formFields["emailAddress"].value : "";

            let responseCode: number | boolean = false;

            // check to see if the user exists
            const userToUpdate = await UserAuthentication.getUserByEmailAddress(controller, email);

            if (userToUpdate.id > 0 && UserAuthentication.userHasFpu(userToUpdate.viewPermissions, Number(producerId))) {

                responseCode = -100;

            } else if (userToUpdate.id > 0) {

                const accessData = MyFpuListFunctions.getUserAccessData(producerId, userToUpdate, userRoleTypes);

                responseCode = await UserFpuAccessHelper.updateUserRoleAccess(accessData);

            } else {

                const accessData = MyFpuListFunctions.getUserRegisterData(producerId, email, userRoleTypes);

                responseCode = await UserFpuAccessHelper.registerUser(accessData);

            }

            switch (responseCode) {

                case 200:
                case 201:
                    notificationType = ToastNotificationType.Success;
                    toastTitle = "Success";

                    if (userToUpdate.id > 0) {
                        toastDescription = "User was successfully granted access.";
                    } else {
                        toastDescription = "Invitation was successfully sent.";
                    }

                    changeView(component, true);

                    break;

                case 400:
                    notificationType = ToastNotificationType.Danger;
                    toastTitle = "Invalid input";
                    toastDescription = "Please try again or contact support if the issue persists.";
                    break;

                case 404:
                    notificationType = ToastNotificationType.Danger;
                    toastTitle = "Resource not found";

                    if (userToUpdate.id > 0) {
                        toastDescription = "Endpoint to update user roles was invalid, try again or contact support.";
                    } else {
                        toastDescription = "Endpoint to register user is invalid, try again or contact support.";
                    }

                    break;

                case 422:
                    notificationType = ToastNotificationType.Danger;
                    toastTitle = "Bad user data";
                    toastDescription = "Unknown data error";
                    break;

                case -100:
                    notificationType = ToastNotificationType.Danger;
                    toastTitle = "Already Exists";
                    toastDescription = "The user with that email address already has access to this FPU.";
                    break;

                default:
                    notificationType = ToastNotificationType.Warning;
                    toastTitle = "Unknown error";
                    toastDescription = "Unable to save user data, contact support.";
                    break;

            }

            CreateToastNotification(toastDispatch, notificationType, toastTitle, toastDescription);

        }

        setIsFormProcessing(false);

    }

    const formValidation = (): boolean => {

        let validForm = true;

        const updatedFields = { ...formFields };

        setRequiredValidated("default");

        // emailAddress
        if (typeof formFields["emailAddress"].value === "string" && (TypeAssertions.isStringNullOrEmpty(formFields["emailAddress"].value) || TypeAssertions.isEmail(formFields["emailAddress"].value) === false)) {

            setRequiredValidated("error");

            updatedFields["emailAddress"].validated = "error";

            validForm = false;

        } else {

            updatedFields["emailAddress"].validated = "default";
        }

        // at least one role was checked.
        const atLeastOne = userRoleTypes.some(roleType => roleType.checked === true);

        if (!atLeastOne) {

            validForm = false;

            setRequiredValidated("error");

            formFields["permissionCheckboxGroup"].validated = "error";

        } else {

            formFields["permissionCheckboxGroup"].validated = "default";

        }

        setFormFields(updatedFields);

        return validForm;
    }

    // get a list of user role types
    React.useEffect(() => {

        const isUserMember = UserAuthentication.userIsMember(userState.user.viewPermissions);

        const isNonMemberRole = !isUserMember;

        /**
         * fpu roles only 
         * ignore master account 
         */
        const filteredRoles: UserRoleTypesInterface[] = roleTypes.filter(roleType => roleType.roleType === "fpu" && roleType.isMasterAccountRole === false && roleType.isNonMemberRole === isNonMemberRole);

        const emptyUser = new UserModel();

        // determine check box values and inactive state
        // non-member users can't change member roles
        // member users can't change non-member roles
        // NOTE: The roles should have been filtered so they logged in user can't see roles they don't have access instead of setting inactive state
        const updatedRoleTypes = MyFpuListFunctions.setRoleTypesCheckedStatus(emptyUser, producerId, filteredRoles, isUserMember);

        setUserRoleTypes(updatedRoleTypes);


    }, [producerId, roleTypes, userState.user.viewPermissions]);

    React.useEffect(() => {

        return () => {

            // on unmount, clear all toast messages
            toastDispatch({
                type: "CLEAR"
            });

        };

    }, [toastDispatch]);

    if (canViewPage) {

        return (
            <ToastNotifications>
                <PageSection>
                    <Button
                        variant="primary"
                        onClick={() => changeView(component)}
                    >
                        Return to User List
                    </Button>
                    <Title headingLevel="h1" className="pf-u-mb-md">Invite User to FPU</Title>

                    <>
                        <Title headingLevel="h2">{producer.alphaName} - {producerId}</Title>

                        <Form autoComplete="false" isWidthLimited>

                            {requiredValidated === "error" && (
                                <FormAlert>
                                    <Alert
                                        variant="danger"
                                        title="Be sure to enter an email address and select at least one role and try again."
                                        aria-live="polite"
                                        role="alert"
                                        isInline
                                    ></Alert>
                                </FormAlert>
                            )}

                            <FormGroup
                                label={formFields["emailAddress"].label}
                                isRequired={formFields["emailAddress"].required}
                                fieldId={formFields["emailAddress"].formFieldId}
                                helperTextInvalid={formFields["emailAddress"].helperTextInvalid}
                                helperTextInvalidIcon={<ExclamationCircleIcon />}
                                validated={formFields["emailAddress"].validated}
                            >
                                <TextInput
                                    isRequired={formFields["emailAddress"].required}
                                    type="email"
                                    id={formFields["emailAddress"].fieldId}
                                    name={formFields["emailAddress"].fieldId}
                                    value={typeof formFields["emailAddress"].value === "string" ? formFields["emailAddress"].value : ""}
                                    onChange={handleChange}
                                />
                            </FormGroup>

                            <FormGroup
                                label={formFields["permissionCheckboxGroup"].label}
                                isRequired={formFields["permissionCheckboxGroup"].required}
                                fieldId={formFields["permissionCheckboxGroup"].fieldId}
                                helperTextInvalid={formFields["permissionCheckboxGroup"].helperTextInvalid}
                                helperTextInvalidIcon={<ExclamationCircleIcon />}
                                validated={formFields["permissionCheckboxGroup"].validated}
                            >
                                <Button variant="link" onClick={handleModalToggle} className="pf-u-p-0">Learn more about permissions</Button>
                                <Modal
                                    variant={ModalVariant.medium}
                                    title="Permissions"
                                    isOpen={isModalOpen}
                                    onClose={handleModalToggle}
                                    actions={[
                                        <Button key="ok" variant="primary" onClick={handleModalToggle}>
                                            OK
                                        </Button>
                                    ]}><UserRoleDescriptions user={userState.user} roleTypes={userRoleTypes} /></Modal>

                                {userRoleTypes.map(roleType => (
                                    <Checkbox
                                        key={roleType.id}
                                        label={roleType.title}
                                        id={`roleid_${roleType.id}`}
                                        name={`roleid_${roleType.id}`}
                                        aria-label={roleType.title}
                                        isChecked={roleType.checked}
                                        isDisabled={!roleType.enabled}
                                        onChange={handleCheckBoxToggle}
                                        value={roleType.id}
                                    />
                                ))}
                            </FormGroup>
                            <ActionGroup>
                                <Button
                                    variant="primary"
                                    onClick={handleSubmit}
                                    spinnerAriaValueText={isFormProcessing ? "Sending" : undefined}
                                    isLoading={isFormProcessing}
                                >{isFormProcessing ? "Sending" : "Invite"}</Button>
                            </ActionGroup>
                        </Form>
                    </>

                </PageSection>
            </ToastNotifications>
        );

    } else {

        return (
            <PermissionDenied />
        );

    }


}

export default FpuAddUserPermissions;
